import { z } from 'zod'

// Helper schemas
const actionId = z.string().regex(/^act_/)
const campaignId = z.string().regex(/^cpn_/)
const dimensionId = z.string().regex(/^dim_/)
const eventId = z.string().regex(/^gev_/)
const goalId = z.string().regex(/^gol_/)
const widgetId = z.string().regex(/^wdg_/)
const offerFunnelId = z.string().regex(/^ofn_/)
const offerId = z.string().regex(/^ofr_/)
const segmentId = z.string().regex(/^(seg_\w+|\$everyone)$/)

// Page schema
const page = z
  .array(
    z.object({
      domain: z.string().default('*'),
      path: z.string().default('*')
    })
  )
  .default([])

// Find/Replace schema for content modifications
// Supports both plain text and regex patterns (e.g., "/pattern/gi")
const findReplaceItemSchema = z.object({
  find: z.string(),      // Plain text OR regex pattern like "/Hello|Hi/gi"
  replace: z.string()    // Replacement text (supports Liquid templates)
})

// Modifications schema for element changes
const modificationsSchema = z.object({
  text: z.string().optional(),                           // Full content replacement
  findReplace: z.array(findReplaceItemSchema).optional() // Find/replace pairs (preserves HTML)
}).passthrough().optional() // passthrough allows additional properties for backward compatibility

// Element Targeting schemas (fingerprint-based element identification)
const fingerprintTextSchema = z.object({
  content: z.string(),
  matchType: z.enum(['exact', 'contains', 'startsWith']).default('exact')
})

const fingerprintVisualSchema = z.object({
  fontSize: z.object({
    min: z.number(),
    max: z.number()
  }).optional(),
  fontWeight: z.object({
    min: z.number()
  }).optional(),
  // REMOVED: positionY - depends on viewport-dependent document height
  isAboveFold: z.boolean().optional()
})

const fingerprintElementSchema = z.object({
  tagName: z.string(),
  candidateTags: z.array(z.string()).default([])
})

// NEW: DOM structure signals (viewport-independent)
const fingerprintStructureSchema = z.object({
  siblingIndex: z.number().optional(),
  siblingCount: z.number().optional(),
  parentChain: z.string().optional(),
  depth: z.number().optional()
})

// NEW: Semantic hints (IDs, data attributes, ARIA, classes)
const fingerprintSemanticsSchema = z.object({
  id: z.string().nullable().optional(),
  dataAttributes: z.record(z.string()).nullable().optional(),
  ariaRole: z.string().nullable().optional(),
  ariaLabel: z.string().nullable().optional(),
  meaningfulClasses: z.array(z.string()).optional()
})

// NEW: Content signals (word count, char count)
const fingerprintContentSchema = z.object({
  charCount: z.number().optional(),
  wordCount: z.number().optional(),
  textPrefix: z.string().optional()
})

const fingerprintSchema = z.object({
  text: fingerprintTextSchema,
  visual: fingerprintVisualSchema.optional(),
  element: fingerprintElementSchema.optional(),
  structure: fingerprintStructureSchema.optional(),   // NEW: DOM structure signals
  semantics: fingerprintSemanticsSchema.optional(),   // NEW: Semantic hints
  content: fingerprintContentSchema.optional()        // NEW: Content signals
})

const targetingSchema = z.object({
  // 'fingerprint': Smart Match - finds element by text content and visual properties
  // 'selector': CSS Selector - traditional targeting via CSS selector
  mode: z.enum(['fingerprint', 'selector']),
  fingerprint: fingerprintSchema.optional(),
  fallbackSelector: z.string().optional(),
  selector: z.string().optional()
})

// LogicDefinition schema

const logicDefinitionItem = z.object({
  $type: z.string(),
  $source: z.string().optional(),
  operator: z.enum(['equals']),
  value: z.string(),
  customFieldId: z.string().optional()
})

const logicDefinition = z.object({}).passthrough()

const nodeSgSchema: z.ZodType<any> = z.object({
  _uuid: z.string().optional(),
  type: z.literal('sg'),
  segmentGroupId: dimensionId.nullable().optional(),
  ask: z.boolean(),
  split: z.union([
    z.literal(false),
    z.array(
      z.object({
        segmentId: segmentId.nullable(),
        child: z.lazy(() => funnelDefinitionNodeRun)
      })
    )
  ])
})

export type NodeSg = z.infer<typeof nodeSgSchema>

const nodeRedirectSchema = z.object({
  _uuid: z.string().optional(),
  type: z.literal('redirect'),
  url: z.string(),
  open: z.boolean()
})

export type NodeRedirect = z.infer<typeof nodeRedirectSchema>

// FunnelDefinitionNode schema
const funnelDefinitionNode: z.ZodType<any> = z.lazy(() =>
  z.discriminatedUnion('type', [
    nodeSgSchema,
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('random'),
      split: z.array(
        z.object({
          child: funnelDefinitionNodeRun
        })
      )
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('yesno'),
      condition: logicDefinition,
      note: z.string().optional(),
      yes: funnelDefinitionNodeRun,
      no: funnelDefinitionNodeRun
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('offer'),
      desiredType: z.enum(['offer', 'form']).optional(),
      offerId: offerId.nullable().optional()
    }),
    nodeRedirectSchema,
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('data'),
      actions: z.array(
        z.object({
          type: z.string(),
          id: actionId,
          score: z.string().optional(),
          adjust: z.number().optional(),
          field: z.string().optional(),
          value: z.string().optional(),
          variableName: z.string().optional(),
          variableValue: z.string().optional(),
          expiresType: z.enum(['never', 'duration']).optional(),
          expiresDuration: z.number().optional(),
          expiresUnit: z.enum(['minutes', 'hours', 'days', 'weeks']).optional()
        })
      )
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('message'),
      content: z.string()
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('highscore'),
      split: z.array(
        z.object({
          score: z.string().nullable(),
          child: funnelDefinitionNodeRun
        })
      )
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('code'),
      name: z.string(),
      code: z.string(),
      selector: z.string().optional()
    }),
    z.object({
      _uuid: z.string().optional(),
      type: z.literal('exit')
    })
  ])
)

const funnelDefinitionNodeRun: z.ZodType<any> = z.array(funnelDefinitionNode)

const funnelDefinitionRootNode = z.object({
  _uuid: z.string().optional(),
  type: z.literal('root'),
  child: funnelDefinitionNodeRun
})

const formSettingsSchema = z.object({
  showLabels: z.boolean()
})

const formFieldSchema = z.object({
  label: z.string().optional(),
  placeholder: z.string().optional(),
  displayType: z.string(),
  width: z.number(),
  options: z.object({}).optional()
})

// TriggeredAction schema
const triggeredAction = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('link'),
    settings: z.object({
      url: z.string().nullable()
    })
  }),
  z.object({
    type: z.literal('tag'),
    settings: z.object({
      partnerName: z.string(),
      tagIds: z.array(z.string()),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('form'),
    settings: z.object({
      partnerName: z.string().nullable(),
      formIds: z.array(z.string()),
      thanksForConvertingText: z.string().optional(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('list'),
    settings: z.object({
      partnerName: z.string(),
      listIds: z.array(z.string()),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('event'),
    settings: z.object({
      partnerName: z.string(),
      eventIds: z.union([z.string(), z.array(z.string())]),
      eventMetadata: z.array(z.object({
        key: z.string(),
        value: z.string()
      })).optional(),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('automation'),
    settings: z.object({
      partnerName: z.string(),
      automationIds: z.union([z.string(), z.array(z.string())]),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  }),
  z.object({
    type: z.literal('contact'),
    settings: z.object({
      partnerName: z.string(),
      thanksForConvertingText: z.string(),
      redirectFunnel: funnelDefinitionRootNode,
      formSettings: formSettingsSchema.optional(),
      fields: z.array(formFieldSchema).optional()
    })
  })
])

const goalEventSchema = z.object({
  id: eventId,
  type: z.string(),
  selector: z.string().optional(),
  page: page
})

// Main schemas
const goalSchema = z.object({
  id: goalId,
  name: z.string(),
  isVisibleToUser: z.boolean().default(true),
  events: z.array(goalEventSchema).default([])
})

const segmentSchema = z.object({
  id: segmentId,
  name: z.string()
})

const dimensionSchema = z.object({
  id: dimensionId,
  name: z.string(),
  semanticType: z.string().nullable(),
  isMultiWinning: z.boolean().default(false),
  favorited: z.boolean().default(false),
  syncTo: z.string().nullable(),
  syncToCustomField: z.string().nullable(),
  syncMode: z.enum(['field', 'tag']).optional().default('field'),
  syncMappings: z.record(z.string()).optional(), // Maps segment IDs to tag/list IDs
  segments: z.array(segmentSchema).default([]),
  signals: z
    .array(
      z.object({
        indicates: segmentId,
        weight: z.string(),
        definition: logicDefinition
      })
    )
    .default([])
})

const variantId = z.string().regex(/^var_/)

const variantSchema = z.object({
  id: variantId,
  name: z.string(),
  rules: z.object({
    all: z.array(z.object({
      dimensionId: dimensionId,
      segment: segmentId
    })).optional(),
    any: z.array(z.object({
      dimensionId: dimensionId,
      segment: segmentId
    })).optional()
  }).optional(),
  actions: z
    .array(
      z.object({
        id: actionId,
        $type: z.string(),
        page: page,
        selector: z.string().optional(),
        targeting: targetingSchema.optional(),
        modifications: modificationsSchema
      })
    )
    .default([])
})

const campaignSchema = z.object({
  id: campaignId,
  name: z.string(),
  is_active: z.boolean().default(true),
  base_url: z.string(),
  recipe_code: z.string().nullable(),
  dimension_id: dimensionId.nullable(),
  goal_ids: z.array(goalId).default([]),
  condition: z.union([z.boolean(), z.object({})]).default(true),
  metadata: z.object({
    generated_by: z.string().optional(),
    source_url: z.string().optional(),
    generated_at: z.string().optional()
  }).optional(),
  testing: z.object({
    is_enabled: z.boolean().default(true),
    withhold: z.number().default(10)
  }),
  variants: z.array(variantSchema).default([]),
  // Keep actions as optional for backward compatibility during migration
  actions: z
    .array(
      z.object({
        id: actionId,
        $type: z.string(),
        page: page,
        selector: z.string().optional(),
        targeting: targetingSchema.optional(),
        variations: z
          .array(
            z.object({
              segment_id: segmentId.optional(),
              name: z.string().optional(),
              rules: z.object({
                all: z.array(z.object({
                  dimensionId: dimensionId,
                  segment: segmentId
                })).optional(),
                any: z.array(z.object({
                  dimensionId: dimensionId,
                  segment: segmentId
                })).optional()
              }).optional(),
              modifications: modificationsSchema
            })
          )
          .default([])
      })
    )
    .optional()
})

// Define the schema for the options
const optionSchema = z.object({
  id: z.string().startsWith('rqo_'),
  segmentId: z.string().startsWith('seg_'),
  label: z.string(),
  conditions: logicDefinition,
  score: z.string().optional(),
  amount: z.number().optional(),
  imageUrl: z.string().optional()
})

// Define the schema for otherOptions
const otherOptionsSchema = z
  .object({
    enabled: z.boolean().default(false),
    otherLabel: z.string().optional().default('Other'),
    inputType: z.string().optional().default('textarea'),
    inputPrompt: z.string().optional(),
    inputPlaceholder: z.string().optional(),
    nextPrompt: z.string().optional().default('Next')
  })
  .nullable()

// Define the main question schema
const questionSchema = z.object({
  id: z.string().startsWith('raq_'),
  text: z.string(),
  dimensionId: z.string().startsWith('dim_').nullable().optional(), // Can be null for input questions
  displayType: z.string().default('buttons'),
  isRandomizingOptionsOrder: z.boolean().default(false),
  otherOptions: otherOptionsSchema,
  options: z.array(optionSchema).default([]),
  displayOptions: z.record(z.any()).default({}),
  isQuestion: z.boolean().optional().default(false), // Flag to identify questions regardless of text content
  inputType: z.string().optional(), // For input questions (text, textarea, number, scale)
  inputSettings: z.record(z.any()).optional(), // For input question settings
  description: z.string().optional(), // For input question descriptions
  syncTo: z.string().optional(), // For input question sync settings
  syncToField: z.string().optional() // For input question field mapping
})

const rightAskSchema = z.object({
  questions: z.array(questionSchema).default([])
})

const widgetOptionsSchema = z.object({
  theme: z.string(),
  palette: z.object({
    borderColor: z.string(),
    contrastColor: z.string(),
    backgroundColor: z.string(),
    textOnContrastColor: z.string(),
    textOnBackgroundColor: z.string()
  }),
  fontSize: z.object({
    lineHeight: z.number(),
    headlineFontSize: z.string(),
    formFieldFontSize: z.string(),
    descriptionFontSize: z.string(),
    answerButtonFontSize: z.string(),
    submitButtonFontSize: z.string()
  }),
  customCss: z.string(),
  placement: z.string().nullable(),
  structure: z.object({
    formFieldSpacing: z.string(),
    buttonBorderRadius: z.string(),
    answerButtonSpacing: z.string(),
    containerBorderWidth: z.string(),
    containerBorderRadius: z.string()
  }),
  displayTargets: z.object({
    phones: z.boolean(),
    desktop: z.boolean(),
    tablets: z.boolean()
  }),
  style: z.object({
    shadowSize: z.string()
  }),
  backButtonLabel: z.string(),
  backButtonEnabled: z.boolean(),
  buttonAnimationTrigger: z.string()
})

const widgetSchema = z.object({
  id: widgetId,
  name: z.string().min(1, 'Name is required'),
  type: z.string().optional(),
  isEnabled: z.boolean().optional(),
  options: widgetOptionsSchema,
  whenToAskNext: z
    .enum(['startover', 'immediately', 'pageview', 'session', 'none'])
    .optional(),
  visibility: z
    .object({
      isWhitelistEnabled: z.boolean(),
      whitelist: page,
      isBlacklistEnabled: z.boolean(),
      blacklist: page,
      trigger: z.string().optional(),
      areas: z.string().optional()
    })
    .optional(),
  contentId: z.union([offerFunnelId, offerId]).nullable().optional()
})

const flowSchema = z.object({
  id: offerFunnelId,
  name: z.string(),
  accessedAt: z.number().optional(),
  funnelDefinition: funnelDefinitionRootNode
})

const offerSchema = z.object({
  id: offerId,
  name: z.string(),
  isEnabled: z.boolean(),
  parts: z.object({
    title: z.string(),
    description: z.string().nullable(),
    buttonText: z.string().nullable(),
    imageUrl: z.string().optional(),
    imageFocalPoint: z
      .object({
        x: z.number().min(0).max(100),
        y: z.number().min(0).max(100)
      })
      .optional()
  }),
  triggeredAction: triggeredAction
})

const rightCtaSchema = z.object({
  settings: z
    .object({
      isEnabled: z.boolean().optional(),
      foregroundColor: z.string().optional(),
      backgroundColor: z.string().optional(),
      buttonForegroundColor: z.string().optional(),
      buttonBackgroundColor: z.string().optional(),
      isBrandingHidden: z.boolean().optional(),
      thanksForAnsweringText: z.string().optional(),
      submittingText: z.string().optional(),
      whyAreWeAskingThisText: z.string().optional(),
      emailFieldLabel: z.string().optional(),
      questionAnswerLabel: z.string().optional(),
      ga: z.boolean().optional(),
      disablePrefillFormFields: z.boolean().optional()
    })
    .optional(),
  widgets: z.array(widgetSchema).optional(),
  offerFunnels: z.array(flowSchema).optional(),
  offers: z.array(offerSchema).optional()
})

const settingsSchema = z.object({
  clientStorage: z.enum(['cookies', 'localStorage']).optional(),
  crossDomainTracking: z.boolean().optional(),
  jsHooks: z.object({}).optional(),
  integrations: z
    .array(z.object({ name: z.string(), enabled: z.boolean() }))
    .default([]),
  enhancedFlowReporting: z.boolean().optional()
})

const phraseSchema = z.object({
  id: z.string(),
  fallbackText: z.string(),
  segmentGroupId: z.string().nullable(),
  map: z.object({})
})

const attributeSchema = z.object({
  label: z.string(),
  value: z.string(),
  internal: z.boolean(),
  overwrite: z.boolean(),
  destination: z.string()
})

// Config schema
const configSchema = z.object({
  id: z.number(),
  version: z.literal('2024-01-01'),
  domainNames: z.array(z.string()).default([]),
  onboarding: z.object({}).default({}),
  settings: settingsSchema.default({}),
  goals: z.array(goalSchema).default([]),
  dimensions: z.array(dimensionSchema).default([]),
  campaigns: z.array(campaignSchema).default([]),
  rightAsk: rightAskSchema.default({}),
  rightCta: rightCtaSchema.optional(),
  phrases: z.array(phraseSchema).optional(),
  attributes: z.array(attributeSchema).optional()
})

// Export types
export type Config = z.infer<typeof configSchema>
export type Goal = z.infer<typeof goalSchema>
export type GoalEvent = z.infer<typeof goalEventSchema>
export type Dimension = z.infer<typeof dimensionSchema>
export type Segment = z.infer<typeof segmentSchema>
export type LogicDefinition = z.infer<typeof logicDefinition>
export type LogicDefinitionItem = z.infer<typeof logicDefinitionItem>
export type Campaign = z.infer<typeof campaignSchema>
export type Variant = z.infer<typeof variantSchema>
export type RightAsk = z.infer<typeof rightAskSchema>
export type Question = z.infer<typeof questionSchema>
export type QuestionOption = z.infer<typeof optionSchema>
export type RightCta = z.infer<typeof rightCtaSchema>
export type Widget = z.infer<typeof widgetSchema>
export type Offer = z.infer<typeof offerSchema>
export type Flow = z.infer<typeof flowSchema>
export type FunnelDefinitionNode = z.infer<typeof funnelDefinitionNode>
export type FunnelDefinitionRootNode = z.infer<typeof funnelDefinitionRootNode>
export type TriggeredAction = z.infer<typeof triggeredAction>
export type Settings = z.infer<typeof settingsSchema>
export type Attribute = z.infer<typeof attributeSchema>
export type Phrase = z.infer<typeof phraseSchema>
export type Targeting = z.infer<typeof targetingSchema>
export type Fingerprint = z.infer<typeof fingerprintSchema>
export type FingerprintText = z.infer<typeof fingerprintTextSchema>
export type FingerprintVisual = z.infer<typeof fingerprintVisualSchema>
export type FingerprintElement = z.infer<typeof fingerprintElementSchema>
export type FingerprintStructure = z.infer<typeof fingerprintStructureSchema>
export type FingerprintSemantics = z.infer<typeof fingerprintSemanticsSchema>
export type FingerprintContent = z.infer<typeof fingerprintContentSchema>
export type FindReplaceItem = z.infer<typeof findReplaceItemSchema>
export type Modifications = z.infer<typeof modificationsSchema>
// Export schemas
export {
  configSchema,
  goalSchema,
  goalEventSchema,
  dimensionSchema,
  campaignSchema,
  rightAskSchema,
  rightCtaSchema,
  flowSchema,
  funnelDefinitionNode,
  funnelDefinitionRootNode,
  triggeredAction,
  questionSchema,
  offerSchema,
  widgetSchema,
  attributeSchema,
  phraseSchema,
  targetingSchema,
  fingerprintSchema,
  findReplaceItemSchema,
  modificationsSchema
}
